// Plan Match Library API client — thin HTTP wrapper around the shared
// library endpoints living at planmatch.generationhealth.me. Lets the
// agent drop its local pm_drugs query + bulk of the provider-network
// scrape and read the answers from one source of truth.
//
// What's wired this session:
//   • searchDrugs           → /api/library/drug-search  (✓ live)
//   • checkProviderNetwork  → /api/library/provider-network (✓ live)
//
//   • rankPlans             → /api/library/rank-plans  (✓ live)
//
// Configure via VITE_PLANMATCH_LIBRARY_URL on the agent's Vercel
// project. Defaults to the consumer's custom domain so dev / preview
// builds work without env-var setup.

import type { LisTier, MedicaidLevel, LivingSetting } from './dual-eligible';

const LIBRARY_URL: string =
  ((import.meta.env as { VITE_PLANMATCH_LIBRARY_URL?: string } | undefined)
    ?.VITE_PLANMATCH_LIBRARY_URL ??
    'https://planmatch.generationhealth.me') as string;

// Canonicalize a plan-id triple so library output and the agent's
// /api/plans output compare cleanly. The library emits the raw
// pm_plans.segment_id ("H1234-005-0") while /api/plans pads empty
// segments to "000" ("H1234-005-000"). Both are valid pointers to the
// same plan — we normalize by stripping leading zeros from the
// segment ("H1234-005-0"). A 2-part id ("H1234-005") gets a "-0"
// appended so combined and triple forms collide on the same key.
export function normalizePlanId(id: string): string {
  if (!id) return id;
  const parts = id.split('-');
  if (parts.length < 2) return id;
  const segment = parts[2] ?? '0';
  const segNormalized = segment.replace(/^0+/, '') || '0';
  return `${parts[0]}-${parts[1]}-${segNormalized}`;
}

// Per-call timeout for library fetches. Rob's 2026-08-09 broker session
// hung on the AgentBase → Plan Match handoff because /api/library/drug-
// search had no client-side timeout; a slow (or cold-started) response
// blocked hydration indefinitely because the meds resolver awaited each
// drug sequentially. 5s bounds a single library call so an outer resolver
// can move on to the "unresolved med with yellow warning" path rather
// than freezing the whole handoff.
const LIBRARY_FETCH_TIMEOUT_MS = 5000;

function withTimeoutSignal(
  external: AbortSignal | undefined,
  timeoutMs: number,
): { signal: AbortSignal; cleanup: () => void } {
  const ctl = new AbortController();
  const onExternalAbort = (): void => ctl.abort(external?.reason);
  if (external) {
    if (external.aborted) ctl.abort(external.reason);
    else external.addEventListener('abort', onExternalAbort, { once: true });
  }
  const timer = setTimeout(
    () => ctl.abort(new DOMException(`library fetch timed out after ${timeoutMs}ms`, 'TimeoutError')),
    timeoutMs,
  );
  return {
    signal: ctl.signal,
    cleanup: () => {
      clearTimeout(timer);
      if (external) external.removeEventListener('abort', onExternalAbort);
    },
  };
}

// ─── drug-search ─────────────────────────────────────────────────

export interface LibraryDrug {
  rxcui: string;
  /** RxNorm canonical name — "levothyroxine sodium 0.025 MG Oral Tablet [Synthroid]". */
  name: string;
  generic_name: string;
  brand_name: string;
  strength: string;
  dose_form: string;
  is_brand: boolean;
  /** Part D / Part B classification + display facts from pm_drugs, returned by
   *  /api/library/drug-search. Optional (older deploys omit them). Threaded into
   *  the Medication so the Meds screen can label OTC / Part B drugs immediately
   *  and the AgentBase sync payload carries real values (not null). */
  part_b_status?: 'part_b' | 'part_b_or_d' | null;
  not_part_d?: boolean;
  strength_display?: string | null;
  package_size?: string | null;
  package_unit?: string | null;
  quantity_unit?: string | null;
}

export async function searchDrugs(
  query: string,
  limit: number = 10,
  signal?: AbortSignal,
): Promise<LibraryDrug[]> {
  const { signal: timedSignal, cleanup } = withTimeoutSignal(signal, LIBRARY_FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`${LIBRARY_URL}/api/library/drug-search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, limit }),
      signal: timedSignal,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`library/drug-search ${res.status} ${body.slice(0, 200)}`);
    }
    const data = (await res.json()) as { drugs?: LibraryDrug[] };
    return data.drugs ?? [];
  } finally {
    cleanup();
  }
}

// ─── provider-network ────────────────────────────────────────────

export interface LibraryProviderPlan {
  plan_id: string; // triple "<contract>-<plan>-<segment>"
  plan_name: string;
  carrier: string;
  // 6-state model (consumer PR #105). not_listed = NPI absent from the
  // carrier directory (✗, eliminates); no_data = no feed for this carrier
  // (grey, kept); error = live lookup failed (kept). Older deploys of the
  // endpoint only ever send in_network/out_of_network/unknown.
  status: 'in_network' | 'out_of_network' | 'not_listed' | 'no_data' | 'error' | 'unknown';
  source: 'fhir_live' | 'cache' | 'medicare_gov' | 'unknown';
  last_verified: string | null;
  /** Raw winning cache source ('broker_verified' | 'fhir_*' | 'healthspring'
   *  | 'medicare_gov' | …) when the library surfaces it, so the agent can
   *  render the exact cross-surface provenance label (provenanceLabel).
   *  Optional: today's /api/library/provider-network collapses `source` to
   *  the 4-value enum above and omits this — the label falls back to the
   *  enum map until the endpoint also returns source_raw. */
  source_raw?: string | null;
  /** Set when the answer was carried over from a prior plan year (no row
   *  for the requested year); drives the "(YYYY network)" label suffix. */
  fallback_year?: number | null;
}

export interface LibraryProviderResult {
  name: string;
  specialty: string;
  plans: LibraryProviderPlan[];
}

export interface LibraryProviderNetworkResponse {
  by_npi: Record<string, LibraryProviderResult>;
}

export async function checkProviderNetwork(args: {
  npis: string[];
  county: string;
  state: string;
  plan_ids?: string[];
  /** Catalog year of the plans asked about. Omitted → the library's date
   *  default (unchanged behavior for existing callers). */
  plan_year?: number;
  signal?: AbortSignal;
}): Promise<LibraryProviderNetworkResponse> {
  const { npis, county, state, plan_ids, plan_year, signal } = args;
  const res = await fetch(`${LIBRARY_URL}/api/library/provider-network`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(
      plan_year != null ? { npis, county, state, plan_ids, plan_year } : { npis, county, state, plan_ids },
    ),
    signal,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      `library/provider-network ${res.status} ${body.slice(0, 200)}`,
    );
  }
  const parsed = (await res.json()) as LibraryProviderNetworkResponse;
  // [AUDIT 1] Raw library response, BEFORE any client-side processing.
  // First 300 chars of the JSON keeps the line scannable while still
  // showing the by_npi keys + first NPI block's plans array.
  try {
    const npiKeys = Object.keys(parsed.by_npi ?? {});
    const firstNpi = npiKeys[0];
    const firstPlans = firstNpi ? parsed.by_npi[firstNpi]?.plans ?? [] : [];
    const inCount = firstPlans.filter((p) => p.status === 'in_network').length;
    console.log(
      `[AUDIT 1] library raw response: npis=${npiKeys.length} firstNpi=${firstNpi ?? '—'} ` +
        `plans=${firstPlans.length} in_network=${inCount} | ` +
        JSON.stringify(parsed).slice(0, 300),
    );
  } catch {
    // Defensive — shouldn't happen, but never let a log break the fetch.
  }
  return parsed;
}

// ─── npi-search ──────────────────────────────────────────────────

export interface LibraryProviderRow {
  npi: string;
  enumeration_type: 'NPI-1' | 'NPI-2';
  first_name: string | null;
  last_name: string;
  credential: string | null;
  specialty: string | null;
  specialty_code: string | null;
  specialty_display: string | null;
  practice_address_1: string | null;
  practice_city: string | null;
  practice_state: string;
  practice_zip: string | null;
  /** Organization name for NPI-2 rows; null for individuals. */
  practice_name: string | null;
  affiliation: string | null;
  is_independent_contractor: boolean;
  display_name: string;
}

export interface LibraryProviderSearchResponse {
  providers: LibraryProviderRow[];
  count: number;
  query: string;
  state: string;
  source: 'nppes' | 'cache';
  fallback?: 'last_name_only' | 'state_dropped';
}

export async function searchProviders(args: {
  query: string;
  state?: string;
  zip?: string;
  city?: string;
  specialty?: string;
  npi?: string;
  limit?: number;
  signal?: AbortSignal;
}): Promise<LibraryProviderSearchResponse> {
  const { signal, ...payload } = args;
  const res = await fetch(`${LIBRARY_URL}/api/library/npi-search`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`library/npi-search ${res.status} ${body.slice(0, 200)}`);
  }
  return (await res.json()) as LibraryProviderSearchResponse;
}

// ─── rank-plans ──────────────────────────────────────────────────

/** A covered brand↔generic equivalent the plan files for a drug it does NOT
 *  cover as entered (brand ≠ generic policy). Non-null only on an uncovered
 *  medication (covered:false); covered meds always carry null. Drives the
 *  "ask your doctor about switching" note. Mirror of the consumer rank-plans
 *  `switch_suggestion` shape — coinsurance is PERCENT (0..100), copay dollars. */
export interface SwitchSuggestion {
  /** true ⇒ the covered equivalent is the generic; false ⇒ the brand. */
  is_generic: boolean;
  /** The covered equivalent's drug_name. */
  name: string | null;
  tier: number | null;
  /** Dollars. */
  copay: number | null;
  /** PERCENT 0..100 (consumer convention — NOT a fraction). */
  coinsurance: number | null;
}

/** Shared formatter for the "ask your doctor about switching" note. One copy
 *  string across all three agent surfaces (Compare grid, Head-to-Head,
 *  QuoteDeliveryV4). Returns null when there's nothing to render. */
export function formatSwitchNote(s: SwitchSuggestion | null | undefined): string | null {
  if (!s || !s.name) return null;
  const kind = s.is_generic ? 'Generic' : 'Brand';
  const tierClause = s.tier != null ? ` at Tier ${s.tier}` : '';
  let costClause = '';
  if (s.copay != null) {
    costClause = `, $${s.copay}/mo`;
  } else if (s.coinsurance != null) {
    costClause = ` (~${s.coinsurance}% coinsurance)`;
  }
  return `${kind} ${s.name} is covered on this plan${tierClause}${costClause} — ask your doctor about switching.`;
}

export interface LibraryRankMedication {
  rxcui: string;
  name: string;
  covered: boolean;
  /** Formulary tier (1–5/6) when covered; null when not on formulary. */
  tier: number | null;
  /** Monthly copay (dollars) — null when coinsurance or unknown. */
  copay: number | null;
  /** Yearly out-of-pocket estimate for this drug on this plan. (At runtime the
   *  consumer sends null for an uncovered "call for price" drug; callers that
   *  care guard for it — the field stays typed `number` for back-compat with
   *  the Compare/LIS consumers that sum it.) */
  annual_cost: number;
  /** CMS utilization-management flags (consumer PR #107). Optional — absent on
   *  older API responses. */
  prior_auth?: boolean;
  step_therapy?: boolean;
  quantity_limit?: boolean;
  quantity_limit_amount?: number | null;
  quantity_limit_days?: number | null;
  /** This drug's member cost Jan..Dec (12 entries). Null/absent for a
   *  call-for-price drug, an Extra Help / Medicaid client, or an older API. */
  monthly_cost?: number[] | null;
  /** A covered brand↔generic equivalent for an UNCOVERED drug (covered:false)
   *  the plan files a clean opposite-form match for. Null on covered meds and
   *  when no equivalent is filed; absent on older API responses. */
  switch_suggestion?: SwitchSuggestion | null;
  /** Part D / Part B coverage classification (consumer rank-plans). Drives the
   *  three drug-coverage labels. All optional — absent on older API responses.
   *  - not_part_d: the drug is excluded from Part D (e.g. OTC); never priced.
   *  - part_b_eligible: the drug is covered under Part B (the plan's Part B
   *    drug coinsurance applies), not a Part D tier/copay.
   *  - part_b_or_d: Part D is the normal path but Part B may cover it in some
   *    situations — keep the Part D price and add a small note. */
  not_part_d?: boolean;
  part_b_eligible?: boolean;
  part_b_or_d?: boolean;
  /** Free-text Part B caveat for a part_b_or_d drug; null when none filed. */
  part_b_note?: string | null;
  /** Human-readable strength + form (e.g. "500 mg tablet"); null when absent. */
  strength_display?: string | null;
}

/** One month of the Jan–Dec drug-cost calendar. */
export interface LibraryRankDrugMonth {
  month: number;
  cost: number;
  /** Part D phase the month ends in; null when no drug runs the timeline. */
  phase: 'deductible' | 'initial' | 'catastrophic' | null;
}

export interface LibraryRankProvider {
  npi: string;
  name: string;
  /** true=in-network, false=out, null=unknown/unverified. */
  in_network: boolean | null;
  /** Provenance of the network determination. */
  source: 'fhir_live' | 'cache' | 'unknown';
}

/** All 17 supplemental-benefit fields the library normalizes from
 *  pm_plan_benefits + pbp_benefits. Each is a free-text display string
 *  or null when the plan doesn't file the category. */
export interface LibraryRankBenefits {
  dental: string | null;
  vision: string | null;
  hearing: string | null;
  otc: string | null;
  fitness: string | null;
  transportation: string | null;
  meals: string | null;
  telehealth: string | null;
  part_b_giveback: string | null;
  pcp_copay: string | null;
  specialist: string | null;
  part_d_deductible: string | null;
  urgent_care: string | null;
  emergency: string | null;
  inpatient: string | null;
  inpatient_mental: string | null;
  skilled_nursing: string | null;
}

export interface LibraryRankPlan {
  /** "<contract>-<plan>-<segment>" triple. */
  plan_id: string;
  plan_name: string;
  carrier: string | null;
  plan_type: string | null;
  premium: number;
  moop: number;
  star_rating: number | null;
  medications: LibraryRankMedication[];
  total_annual_drug_cost: number;
  /** Jan–Dec drug-cost calendar (12 rows, sums to total_annual_drug_cost).
   *  Null for Extra Help / Medicaid clients; absent on older API responses. */
  drug_cost_by_month?: LibraryRankDrugMonth[] | null;
  /** Whole-plan yearly cost: premium + drugs − Part B giveback + the brain's
   *  expected medical copays from the client's usage estimate. Decides the
   *  Head to Head winner. Absent on older API responses. */
  total_annual_cost_with_medical?: number;
  meds_covered: number;
  meds_total: number;
  providers: LibraryRankProvider[];
  docs_in_network: number;
  docs_total: number;
  benefits: LibraryRankBenefits;
  total_annual_cost: number;
  /** 1–4 for Top picks, null for bench. */
  slot: number | null;
  ribbon: string | null;
  gate_results: {
    gate1_passed: boolean;
    gate2_passed: boolean;
    gate3_passed: boolean;
    /** Lower-copays gate (optional — absent on older API responses). */
    gate4_passed?: boolean;
  };
  /** Sellability — false ⇒ non-commissionable (UHC/Devoted flag, exclude
   *  rule, or Wellcare non-allowlist). Never in the top four; shown on the
   *  bench marked "Not sellable" with enroll blocked. Optional: absent on
   *  older API responses ⇒ treated as sellable. */
  sellable?: boolean;
  sellable_reason?: string | null;
}

export interface LibraryRankResult {
  top_plans: LibraryRankPlan[];
  bench_plans: LibraryRankPlan[];
  /** The client's current plan, priced but never ranked (the brain keeps it
   *  out of the pool). Null/absent when none is on file. */
  current_plan?: LibraryRankPlan | null;
  total_plans_in_county: number;
  gate1_survivors: number;
  gate2_survivors: number;
  gate3_survivors: number;
  gate4_survivors?: number;
  detected_conditions: string[];
  csnp_eligible: boolean;
  dsnp_eligible: boolean;
  /** TPMO (e)(41) counts computed from the sellable county set. */
  tpmo_org_count?: number;
  tpmo_product_count?: number;
  /** Fixed CMS-safe copy when the county can't fill four sellable plans. */
  sellable_shortfall_message?: string | null;
  /** Gate-3-surviving sellable plans that are coinsurance-only on a Gate-4
   *  copay service (reported per county). */
  copay_coinsurance_only_count?: number;
}

/** The 4 CMS-recognized C-SNP qualifying conditions the brain
 *  accepts as a self-report. Anything outside this enum is dropped
 *  server-side before reaching the brain. */
export type CsnpConditionKey = 'diabetes' | 'cardio' | 'copd' | 'esrd';

export interface LibraryRankInput {
  county: string;
  zip: string;
  state: string;
  medications: { rxcui: string; name: string; strength?: string }[];
  providers: { npi: string; name: string }[];
  /** Extras input — up to 3 entries in rank order (P1/P2/P3). P1 hard-
   *  eliminates plans that don't offer it; P2 and P3 contribute to the
   *  brain's gate3Score, which breaks cost ties within $500/yr. Legacy
   *  `{ type, enabled }` shape still accepted by the server, but the
   *  agent app now sends the priority-shaped payload. */
  extras: {
    type: string;
    /** New priority-shaped payload (post-2026-07 gate 3). */
    priority?: 1 | 2 | 3;
    /** Legacy on/off flag — server maps first 3 enabled entries in
     *  insertion order to P1/P2/P3. Kept optional to preserve backward
     *  compat while the agent app finishes migrating. */
    enabled?: boolean;
    threshold?: number;
  }[];
  /** Self-reported chronic conditions the broker captured. Routes the
   *  brain's C-SNP eligibility + reserved-slot path so users with no
   *  qualifying meds still surface chronic-condition plans (the May 23
   *  diabetes-without-Ozempic break). Optional — empty means
   *  "med-detection only". */
  csnpConditions?: CsnpConditionKey[];
  current_plan_id?: string | null;
  /** Self-reported dual-eligibility (Medicaid + Medicare). When true,
   *  the library brain's population gate keeps D-SNP plans in the
   *  Top-4 candidate pool; when false / undefined, D-SNPs are stripped
   *  for the standard recommendation flow. Maps to UserProfile
   *  .dsnpEligible on the server side. */
  dsnp_eligible?: boolean;
  /** CMS enrollment period the beneficiary is using. Forwarded to the
   *  library brain so it can compute enrollmentGated (window-shopping
   *  vs enroll-eligible) and attach the compliance label. Undefined
   *  means the front-end hasn't captured it — brain runs unwarned. */
  enrollment_period?: 'IEP' | 'ICEP' | 'SEP' | 'OEP' | 'AEP';
  /** CMS SEP reason code, auto-derived from the plain-English life
   *  event upstream. Only meaningful when enrollment_period === 'SEP'.
   *  Never populated from a raw CMS-code dropdown (SEP fraud gate). */
  sep_reason_code?: string;
  /** Beneficiary Low-Income Subsidy (Extra Help) tier. Currently
   *  passed through for forward compatibility — the library rank-plans
   *  server ignores it until the consumer repo lands LIS cap application
   *  server-side. The agent applies LIS caps client-side over the
   *  returned rank result (see src/lib/lis-cap-agent-v3.ts). */
  lis_tier?: LisTier;
  /** Medicaid category (drives QMB medical-cost zeroing + FBDE
   *  LIS auto-deeming when lis_tier is undefined). Same
   *  forward-compat status as lis_tier. */
  medicaid_level?: MedicaidLevel;
  /** Community vs institutional/HCBS. Only affects auto-deeming for
   *  FBDE beneficiaries. Same forward-compat status as lis_tier. */
  living_setting?: LivingSetting;
  /** rxcui → live Medicare.gov gross monthly price for the client's
   *  quantity (src/lib/drugGrossPrice). Prices drugs pm_drug_cost_cache
   *  doesn't carry instead of the flat per-tier notional. */
  grossMonthlyByRxcui?: Record<string, number>;
  /** Pharmacy dispensing channel for drug-cost pricing:
   *  `preferred_retail` (default, 30-day fills) or `mail_order` (90-day
   *  fills). Forwarded to the library rank-plans brain; omitted ⇒
   *  preferred_retail. No `standard_retail` — pm_plan_benefits files only
   *  the preferred 30-day retail cost-share. */
  pharmacy?: 'preferred_retail' | 'mail_order';
}

export async function rankPlans(
  input: LibraryRankInput,
  signal?: AbortSignal,
): Promise<LibraryRankResult> {
  const res = await fetch(`${LIBRARY_URL}/api/library/rank-plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    signal,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`library/rank-plans ${res.status} ${body.slice(0, 200)}`);
  }
  return (await res.json()) as LibraryRankResult;
}

// ─── broker-verify-provider ───────────────────────────────────────
//
// Persists a "Mark In-Network" click to pm_provider_network_cache as
// a source='broker_verified' row. Next rank-plans response picks it
// up via the cache's freshest-checked_at resolution. FHIR-source rows
// (fhir_uhc / fhir_humana / fhir_devoted / fhir_bcbsnc) are
// authoritative — the server returns
// overwritten='skipped_fhir_authoritative' rather than overwriting.

export interface BrokerVerifyProviderResult {
  success: true;
  overwritten: 'inserted' | 'skipped_fhir_authoritative';
  checked_at?: string;
  fhir_source?: string;
  fhir_covered?: boolean;
}

export async function brokerVerifyProvider(args: {
  npi: string;
  /** Triple plan id — "H5253-189-000" form (matches the plan.id the
   *  agent threads everywhere). The endpoint splits it into combined
   *  contract+plan and segment internally. */
  planId: string;
  signal?: AbortSignal;
}): Promise<BrokerVerifyProviderResult> {
  const { npi, planId, signal } = args;
  const res = await fetch(
    `${LIBRARY_URL}/api/library/broker-verify-provider`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ npi, planId }),
      signal,
    },
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      `library/broker-verify-provider ${res.status} ${body.slice(0, 200)}`,
    );
  }
  return (await res.json()) as BrokerVerifyProviderResult;
}
